import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';

interface SourceResult {
  document_id?: number;
  document_name?: string;
  document_title?: string;
  page_number?: number;
  page?: number;
  pages?: number[];
  content?: string;
  text?: string;
  similarity?: number;
}

interface SourceAccordionProps {
  sources: SourceResult[];
  onSourceClick?: (documentId: number, documentName: string, pageNumber?: number) => void;
}

function formatPageRange(pages: number[]): string {
  if (pages.length === 0) return '';
  if (pages.length === 1) return `p.${pages[0]}`;

  // Build compact ranges: [1,2,3,5,7,8] → "p.1-3, 5, 7-8"
  const sorted = [...pages].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      parts.push(start === end ? `${start}` : `${start}-${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  parts.push(start === end ? `${start}` : `${start}-${end}`);
  return `p.${parts.join(', ')}`;
}

const SourceAccordion: React.FC<SourceAccordionProps> = ({ sources, onSourceClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" />
        {sources.length} source{sources.length !== 1 ? 's' : ''}
        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-1.5 border-l-2 border-gray-200 dark:border-gray-600 pl-3">
          {sources.map((source, index) => {
            const docName = source.document_title || source.document_name || 'Unknown Document';
            const pages = source.pages;
            const pageNum = source.page_number || source.page;
            const snippet = source.content || source.text || '';
            const docId = source.document_id;

            // Format page display: use pages array if available, fall back to single page
            const pageDisplay = pages && pages.length > 0
              ? formatPageRange(pages)
              : pageNum ? `p.${pageNum}` : '';

            return (
              <button
                key={index}
                onClick={() => docId && onSourceClick?.(docId, docName, pageNum)}
                className="block w-full text-left p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="text-xs font-medium text-blue-600 dark:text-blue-400 break-words">
                  {docName}
                  {pageDisplay && <span className="text-gray-400 dark:text-gray-500"> · {pageDisplay}</span>}
                </div>
                {snippet && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                    {snippet.slice(0, 120)}{snippet.length > 120 ? '...' : ''}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SourceAccordion;
